import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, API, fileUrl } from "@/lib/api";
import { Btn, Card, Label, Input, Textarea, Select, StatusBadge, Attachment } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, FileDown, Calculator, Mail, Sparkles, Wrench, Receipt, DollarSign } from "lucide-react";
import { useTranslation } from "react-i18next";

const BID_STATUSES = ["draft", "sent", "approved", "invoice", "declined"];
const lineKey = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Bids() {
  const { t } = useTranslation();
  const [bids, setBids] = useState([]);
  const [priceBook, setPriceBook] = useState([]);
  const [clientDir, setClientDir] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get("/bids").then((r) => setBids(r.data)), []);
  useEffect(() => { load(); api.get("/pricebook").then((r) => setPriceBook(r.data)); api.get("/client-directory").then((r) => setClientDir(r.data)); }, [load]);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && bids.length) {
      const b = bids.find((x) => x.id === openId);
      if (b) { setEditing(b); setSearchParams({}, { replace: true }); }
    }
  }, [searchParams, bids, setSearchParams]);

  const openNew = () => setEditing({ title: "", client_name: "", client_email: "", property_address: "", line_items: [], tax_rate: 7.5, fee: 0, status: "draft", notes: "", photo_ids: [], source_client_id: "", source_client_name: "" });

  const grouped = BID_STATUSES.map((s) => ({ status: s, items: bids.filter((b) => b.status === s) }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 animate-rise">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">{t("bidsPage.eyebrow")}</div>
          <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">{t("bidsPage.title")}</h1>
        </div>
        <Btn data-testid="new-bid-btn" onClick={openNew}><Plus size={16} /> {t("bidsPage.newBid")}</Btn>
      </div>

      <ToolsBar />

      <InvoiceSummary bids={bids} />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {grouped.map((col) => (
          <div key={col.status} className="space-y-3" data-testid={`pipeline-col-${col.status}`}>
            <div className="flex items-center justify-between">
              <StatusBadge status={col.status} />
              <span className="font-mono text-xs text-zinc-600">{col.items.length}</span>
            </div>
            {col.items.length === 0 && <div className="text-zinc-700 font-mono text-xs border-2 border-dashed border-zinc-900 p-4 text-center">Empty</div>}
            {col.items.map((b) => (
              <Card key={b.id} data-testid={`bid-card-${b.id}`} onClick={() => setEditing(b)} className="p-4 cursor-pointer hover:-translate-y-1 hover:shadow-[5px_5px_0px_#FF5722] transition-all">
                <h3 className="font-head font-bold text-white text-sm leading-tight">{b.title}</h3>
                <div className="font-mono text-[11px] text-zinc-500 mt-1">{b.client_name || "—"}</div>
                <div className="font-head font-black text-2xl text-[#FF5722] mt-2">{money(b.totals?.total)}</div>
                <div className="font-mono text-[10px] text-zinc-600">{b.line_items?.length || 0} line items</div>
                {b.status === "invoice" && <div className="mt-2 flex items-center gap-2 flex-wrap"><PaymentBadge totals={b.totals} />{b.totals?.margin != null && <span data-testid={`margin-${b.id}`} className={`font-mono text-[10px] px-1.5 py-0.5 border ${b.totals.margin < 0 ? "text-red-400 border-red-900" : "text-emerald-400 border-emerald-900"}`}>Margin {money(b.totals.margin)} · {b.totals.margin_pct}%</span>}</div>}
              </Card>
            ))}
          </div>
        ))}
      </div>

      {editing && <BidEditor bid={editing} priceBook={priceBook} clientDir={clientDir} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function ToolsBar() {
  const [open, setOpen] = useState(false);
  const [cy, setCy] = useState({ l: "", w: "", d: "" });
  const [vt, setVt] = useState({ rooms: "", sqft: "" });
  const cubicYards = (Number(cy.l || 0) * Number(cy.w || 0) * Number(cy.d || 0) / 27).toFixed(2);
  const vtPrice = (Number(vt.rooms || 0) * 25 + Number(vt.sqft || 0) * 0.08).toFixed(2);
  return (
    <Card className="p-4">
      <button data-testid="tools-toggle" onClick={() => setOpen(!open)} className="flex items-center gap-2 font-head font-bold uppercase text-sm text-white"><Wrench size={16} className="text-[#FF5722]" /> Estimator Tools {open ? "▾" : "▸"}</button>
      {open && (
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div className="bg-zinc-950 border-2 border-zinc-800 p-3">
            <Label>Cubic Yard Calculator (feet)</Label>
            <div className="grid grid-cols-3 gap-2">
              <Input data-testid="cy-l" type="number" placeholder="L" value={cy.l} onChange={(e) => setCy({ ...cy, l: e.target.value })} />
              <Input data-testid="cy-w" type="number" placeholder="W" value={cy.w} onChange={(e) => setCy({ ...cy, w: e.target.value })} />
              <Input data-testid="cy-d" type="number" placeholder="Depth" value={cy.d} onChange={(e) => setCy({ ...cy, d: e.target.value })} />
            </div>
            <div className="mt-2 font-head font-black text-xl text-[#FF5722]" data-testid="cy-result">{cubicYards} yd³</div>
            <div className="font-mono text-[10px] text-zinc-500">L×W×Depth ÷ 27 = {(Number(cy.l || 0) * Number(cy.w || 0) * Number(cy.d || 0)).toFixed(0)} ft³</div>
          </div>
          <div className="bg-zinc-950 border-2 border-zinc-800 p-3">
            <Label>Virtual Tour Estimate</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="vt-rooms" type="number" placeholder="Rooms" value={vt.rooms} onChange={(e) => setVt({ ...vt, rooms: e.target.value })} />
              <Input data-testid="vt-sqft" type="number" placeholder="Sq ft" value={vt.sqft} onChange={(e) => setVt({ ...vt, sqft: e.target.value })} />
            </div>
            <div className="mt-2 font-head font-black text-xl text-[#FF5722]" data-testid="vt-result">${vtPrice}</div>
            <div className="font-mono text-[10px] text-zinc-500">$25/room + $0.08/sq ft</div>
          </div>
        </div>
      )}
    </Card>
  );
}

function PaymentBadge({ totals }) {
  const s = totals?.payment_status || "unpaid";
  const map = { paid: "bg-emerald-500 text-black", partial: "bg-amber-500 text-black", unpaid: "bg-zinc-700 text-white" };
  const label = s === "paid" ? "Paid" : s === "partial" ? `Partial · ${money(totals.balance)} due` : "Unpaid";
  return <span data-testid="payment-badge" className={`inline-block font-mono font-bold text-[10px] uppercase tracking-wider px-2 py-0.5 border border-black ${map[s]}`}>{label}</span>;
}

function InvoiceSummary({ bids }) {
  const inv = bids.filter((b) => b.status === "invoice");
  if (inv.length === 0) return null;
  const collected = inv.reduce((s, b) => s + (b.totals?.paid || 0), 0);
  const outstanding = inv.reduce((s, b) => s + (b.totals?.balance || 0), 0);
  return (
    <Card className="p-4 flex flex-wrap gap-8" data-testid="invoice-summary">
      <div><div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Collected</div><div className="font-head font-black text-2xl text-emerald-400">{money(collected)}</div></div>
      <div><div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Outstanding</div><div className="font-head font-black text-2xl text-[#FF5722]">{money(outstanding)}</div></div>
      <div><div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Invoices</div><div className="font-head font-black text-2xl text-white">{inv.length}</div></div>
    </Card>
  );
}

function BidEditor({ bid, priceBook, clientDir, onClose, onSaved }) {
  const [form, setForm] = useState({ ...bid, line_items: (bid.line_items || []).map((l) => ({ ...l, _key: l._key || lineKey() })) });
  const [id, setId] = useState(bid.id || null);
  const [pbSel, setPbSel] = useState("");
  const [aiDesc, setAiDesc] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiCT, setAiCT] = useState("Homeowner");
  const [confirmDel, setConfirmDel] = useState(false);
  const [payAmt, setPayAmt] = useState("");
  const [payMethod, setPayMethod] = useState("Check");

  const aiGenerate = async () => {
    if (!aiDesc.trim()) { toast.error("Describe the job first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/estimate", { description: aiDesc, client_type: aiCT });
      if (!data.line_items?.length) { toast.error("AI couldn't extract line items — add more detail"); return; }
      setForm((f) => ({ ...f, line_items: [...f.line_items, ...data.line_items.map((l) => ({ ...l, _key: lineKey() }))], notes: f.notes || data.notes || "" }));
      toast.success(`Added ${data.line_items.length} AI line item(s)`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const photoScope = async () => {
    if (!form.photo_ids?.length) { toast.error("Attach job photos first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/public/ai/photo-scope", { photo_ids: form.photo_ids, service_type: "", client_type: aiCT, notes: aiDesc });
      if (data.line_items?.length) setForm((f) => ({ ...f, line_items: [...f.line_items, ...data.line_items.map((l) => ({ ...l, _key: lineKey() }))], notes: f.notes || data.scope || "" }));
      toast.success("AI analyzed the photos");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const setLine = (idx, key, val) => {
    const items = [...form.line_items];
    items[idx] = { ...items[idx], [key]: key === "name" ? val : parseFloat(val) || 0 };
    setForm({ ...form, line_items: items });
  };
  const addLine = () => setForm({ ...form, line_items: [...form.line_items, { name: "", quantity: 1, unit_price: 0, _key: lineKey() }] });
  const addFromBook = () => {
    const item = priceBook.find((p) => p.id === pbSel);
    if (!item) return;
    setForm({ ...form, line_items: [...form.line_items, { name: item.name, quantity: 1, unit_price: item.unit_price, _key: lineKey() }] });
    setPbSel("");
  };
  const removeLine = (idx) => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });

  const subtotal = form.line_items.reduce((s, l) => s + (l.quantity || 0) * (l.unit_price || 0), 0);
  const tax = subtotal * ((form.tax_rate || 0) / 100);
  const total = subtotal + tax + (parseFloat(form.fee) || 0);

  const save = async () => {
    try {
      const payload = { title: form.title, client_name: form.client_name, client_email: form.client_email || "", property_address: form.property_address, line_items: form.line_items.map(({ _key, ...l }) => l), tax_rate: parseFloat(form.tax_rate) || 0, fee: parseFloat(form.fee) || 0, status: form.status, notes: form.notes, photo_ids: form.photo_ids || [], source_client_id: form.source_client_id || null, source_client_name: form.source_client_name || null, contractor_payout: form.contractor_payout == null || form.contractor_payout === "" ? null : Number(form.contractor_payout) };
      let res;
      if (id) res = await api.put(`/bids/${id}`, payload);
      else res = await api.post("/bids", payload);
      setId(res.data.id);
      toast.success("Bid saved");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const exportPdf = async () => {
    if (!id) { toast.error("Save the bid first"); return; }
    const token = localStorage.getItem("ss_token");
    window.open(`${API}/bids/${id}/pdf?auth=${token}`, "_blank");
  };

  const sendEmail = async () => {
    if (!id) { toast.error("Save the bid first"); return; }
    if (!form.client_email) { toast.error("Add a client email first"); return; }
    try {
      await api.post(`/bids/${id}/send`);
      toast.success(`Estimate emailed to ${form.client_email}`);
      setForm((f) => ({ ...f, status: "sent" }));
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doDelete = async () => {
    if (!id) { onClose(); return; }
    try {
      await api.delete(`/bids/${id}`);
      toast.success("Bid deleted");
      setConfirmDel(false);
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const convertInvoice = async () => {
    if (!id) { toast.error("Save the bid first"); return; }
    try {
      const { data } = await api.post(`/bids/${id}/convert-to-invoice`);
      toast.success(data.job_marked_ready ? "Invoice created & linked job marked ready" : "Invoice created from this bid");
      setForm((f) => ({ ...f, status: "approved" }));
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const recordPayment = async (amount) => {
    if (!id) return;
    const amt = amount != null ? amount : parseFloat(payAmt);
    if (!amt || amt <= 0) { toast.error("Enter a payment amount"); return; }
    try {
      const { data } = await api.post(`/bids/${id}/payments`, { amount: amt, method: payMethod });
      setForm((f) => ({ ...f, payments: data.payments || [] }));
      setPayAmt("");
      toast.success("Payment recorded");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deletePayment = async (pid) => {
    try {
      const { data } = await api.delete(`/bids/${id}/payments/${pid}`);
      setForm((f) => ({ ...f, payments: data.payments || [] }));
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#141417] border-2 border-[#FF5722] text-white max-w-2xl max-h-[90vh] overflow-y-auto rounded-none">
        <DialogHeader><DialogTitle className="font-head font-black uppercase flex items-center gap-2"><Calculator size={20} className="text-[#FF5722]" /> {id ? "Edit Bid" : "New Bid"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Title</Label><Input data-testid="bid-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Client</Label><Input data-testid="bid-client" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
            <div><Label>Client Email</Label><Input data-testid="bid-email" type="email" value={form.client_email || ""} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="client@email.com" /></div>
            <div><Label>Property</Label><Input data-testid="bid-property" value={form.property_address} onChange={(e) => setForm({ ...form, property_address: e.target.value })} /></div>
            <div><Label>Status</Label><Select data-testid="bid-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{BID_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</Select></div>
            <div className="sm:col-span-2"><Label>Client Account (CRM) — saves this bid on their profile</Label><Select data-testid="bid-source-client" value={form.source_client_id || ""} onChange={(e) => { const c = (clientDir || []).find((x) => x.id === e.target.value); setForm({ ...form, source_client_id: e.target.value, source_client_name: c ? (c.company || c.name) : "", client_name: c ? (c.company || c.name) : form.client_name, client_email: c?.email || form.client_email }); }}><option value="">— Not linked —</option>{(clientDir || []).map((c) => <option key={c.id} value={c.id}>{c.company ? `${c.company} (${c.name})` : c.name}</option>)}</Select></div>
          </div>

          {/* Smart Estimate (AI) */}
          <div className="bg-[#FF5722]/5 border-2 border-[#FF5722]/30 p-3 space-y-2">
            <Label>✨ Smart Estimate — describe the job</Label>
            <Select data-testid="ai-client-type" value={aiCT} onChange={(e) => setAiCT(e.target.value)}>{["Homeowner", "Realtor", "Investor", "Property Manager", "Bank/REO"].map((c) => <option key={c}>{c}</option>)}</Select>
            <Textarea data-testid="ai-estimate-desc" value={aiDesc} onChange={(e) => setAiDesc(e.target.value)} placeholder="e.g. Winterize a 3-bed vacant home, change 2 locks, remove 6 cu yd of debris, and shoot listing photos" className="min-h-[70px]" />
            <div className="flex gap-2 flex-wrap">
              <Btn variant="secondary" data-testid="ai-estimate-btn" onClick={aiGenerate} disabled={aiBusy}><Sparkles size={14} /> {aiBusy ? "Working…" : "Generate from Text"}</Btn>
              {form.photo_ids?.length > 0 && <Btn variant="secondary" data-testid="ai-photo-btn" onClick={photoScope} disabled={aiBusy}><Sparkles size={14} /> Analyze {form.photo_ids.length} Photo(s)</Btn>}
            </div>
          </div>

          {/* Line items */}
          <div>
            <Label>Line Items</Label>
            <div className="space-y-2">
              {form.line_items.map((l, i) => (
                <div key={l._key || i} className="flex gap-2 items-center" data-testid={`bid-line-${i}`}>
                  <Input value={l.name} onChange={(e) => setLine(i, "name", e.target.value)} placeholder="Description" className="flex-1" />
                  <Input type="number" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className="w-16" />
                  <Input type="number" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} className="w-24" />
                  <button onClick={() => removeLine(i)} className="text-red-500 hover:text-red-400 px-1" data-testid={`remove-line-${i}`}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2 flex-wrap">
              <Btn variant="secondary" data-testid="add-line-btn" onClick={addLine}><Plus size={14} /> Blank Line</Btn>
              <div className="flex gap-2 flex-1 min-w-[220px]">
                <Select value={pbSel} onChange={(e) => setPbSel(e.target.value)} data-testid="pricebook-select"><option value="">From price book…</option>{priceBook.map((p) => <option key={p.id} value={p.id}>{p.name} (${p.unit_price})</option>)}</Select>
                <Btn variant="secondary" data-testid="add-from-book-btn" onClick={addFromBook} disabled={!pbSel}>Add</Btn>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tax Rate (%)</Label><Input type="number" data-testid="bid-tax" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} /></div>
            <div><Label>Fee ($)</Label><Input type="number" data-testid="bid-fee" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} /></div>
          </div>

          <div><Label>Notes</Label><Textarea data-testid="bid-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

          {form.photo_ids?.length > 0 && (
            <div data-testid="bid-photos">
              <Label>Attached Job Photos ({form.photo_ids.length})</Label>
              <div className="flex gap-2 flex-wrap">
                {form.photo_ids.map((pid) => (
                  <div key={pid} className="relative">
                    <Attachment src={fileUrl(pid)} size={56} className="object-cover border-2 border-zinc-800" />
                    <button type="button" onClick={() => setForm((f) => ({ ...f, photo_ids: f.photo_ids.filter((x) => x !== pid) }))} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white w-4 h-4 flex items-center justify-center border border-black text-[10px] leading-none">×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Totals */}
          <Card className="p-4 bg-zinc-950">
            <div className="flex justify-between text-sm text-zinc-400"><span>Subtotal</span><span className="font-mono">{money(subtotal)}</span></div>
            <div className="flex justify-between text-sm text-zinc-400"><span>Tax ({form.tax_rate || 0}%)</span><span className="font-mono">{money(tax)}</span></div>
            <div className="flex justify-between text-sm text-zinc-400"><span>Fee</span><span className="font-mono">{money(parseFloat(form.fee) || 0)}</span></div>
            <div className="flex justify-between font-head font-black text-xl text-[#FF5722] mt-2 pt-2 border-t-2 border-zinc-800"><span>TOTAL</span><span data-testid="bid-total">{money(total)}</span></div>
            {(form.status === "invoice" || form.source_job_id) && (
              <div className="mt-3 pt-3 border-t border-zinc-800 space-y-2" data-testid="payout-margin">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-zinc-400">Contractor payout{form.contractor_name ? <span className="text-zinc-600"> · {form.contractor_name}</span> : ""}</span>
                  <div className="flex items-center gap-1 font-mono"><span className="text-zinc-500">$</span><Input data-testid="invoice-payout" type="number" step="0.01" value={form.contractor_payout ?? ""} onChange={(e) => setForm({ ...form, contractor_payout: e.target.value === "" ? null : Number(e.target.value) })} placeholder="0.00" className="w-28 text-right" /></div>
                </div>
                {form.contractor_payout != null && (() => {
                  const margin = subtotal - Number(form.contractor_payout);
                  const pct = subtotal ? (margin / subtotal) * 100 : 0;
                  return (
                    <div className={`flex justify-between text-sm font-mono ${margin < 0 ? "text-red-400" : "text-emerald-400"}`} data-testid="invoice-margin">
                      <span>Your margin</span><span>{money(margin)} · {pct.toFixed(1)}%</span>
                    </div>
                  );
                })()}
                <p className="text-[11px] text-zinc-600">Agreed payout is carried over from the job automatically; adjust here if it changed. Never shown to clients.</p>
              </div>
            )}
          </Card>

          {id && form.status === "invoice" && (() => {
            const pay = form.payments || [];
            const paidTotal = pay.reduce((s, p) => s + (p.amount || 0), 0);
            const balance = Math.max(0, total - paidTotal);
            const pstatus = paidTotal >= total && total > 0 ? "paid" : paidTotal > 0 ? "partial" : "unpaid";
            const payTotals = { total, paid: paidTotal, balance, payment_status: pstatus };
            return (
              <Card className="p-4 bg-zinc-950 space-y-3" data-testid="payments-card">
                <div className="flex items-center justify-between">
                  <span className="font-head font-bold uppercase text-sm text-white flex items-center gap-1.5"><DollarSign size={15} className="text-[#FF5722]" /> Payments</span>
                  <PaymentBadge totals={payTotals} />
                </div>
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div><div className="text-zinc-500">Invoice</div><div className="font-mono text-white">{money(total)}</div></div>
                  <div><div className="text-zinc-500">Paid</div><div className="font-mono text-emerald-400">{money(paidTotal)}</div></div>
                  <div><div className="text-zinc-500">Balance</div><div className="font-mono text-[#FF5722]">{money(balance)}</div></div>
                </div>
                {pay.length > 0 && (
                  <div className="space-y-1">
                    {pay.map((p) => (
                      <div key={p.id} data-testid={`payment-${p.id}`} className="flex items-center justify-between text-xs border border-zinc-800 px-2 py-1">
                        <span className="text-zinc-300">{money(p.amount)} · {p.method}{p.note ? ` · ${p.note}` : ""} <span className="text-zinc-600">{new Date(p.date).toLocaleDateString()}</span></span>
                        <button onClick={() => deletePayment(p.id)} className="text-red-500 hover:text-red-400" data-testid={`del-payment-${p.id}`}><Trash2 size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 items-end flex-wrap">
                  <div className="flex-1 min-w-[90px]"><Label>Amount</Label><Input data-testid="pay-amount" type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} placeholder={balance.toFixed(2)} /></div>
                  <div className="w-28"><Label>Method</Label><Select data-testid="pay-method" value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>{["Check", "Card", "Cash", "ACH", "Other"].map((m) => <option key={m}>{m}</option>)}</Select></div>
                  <Btn data-testid="record-payment-btn" onClick={() => recordPayment()}>Record</Btn>
                  {balance > 0 && <Btn variant="secondary" data-testid="mark-paid-btn" onClick={() => recordPayment(balance)}>Mark Paid in Full</Btn>}
                </div>
              </Card>
            );
          })()}

          <div className="flex gap-2 flex-wrap">
            <Btn data-testid="save-bid-btn" onClick={save} className="flex-1">Save Bid</Btn>
            <Btn variant="secondary" data-testid="email-bid-btn" onClick={sendEmail}><Mail size={16} /> Email to Client</Btn>
            <Btn variant="secondary" data-testid="export-pdf-btn" onClick={exportPdf}><FileDown size={16} /> Export PDF</Btn>
            {id && form.status !== "invoice" && <Btn data-testid="convert-invoice-btn" onClick={convertInvoice}><Receipt size={16} /> Convert to Invoice</Btn>}
            {id && <Btn variant="danger" data-testid="delete-bid-btn" onClick={() => setConfirmDel(true)}><Trash2 size={16} /> Delete</Btn>}
          </div>
        </div>

        <Dialog open={confirmDel} onOpenChange={setConfirmDel}>
          <DialogContent className="bg-[#141417] border-2 border-red-600 text-white rounded-none">
            <DialogHeader><DialogTitle className="font-head font-black uppercase text-red-400">Delete bid?</DialogTitle></DialogHeader>
            <p className="text-sm text-zinc-300">Permanently delete <span className="text-white font-semibold">{form.title || "this bid"}</span>? This cannot be undone.</p>
            <div className="flex gap-2 justify-end mt-2">
              <Btn variant="secondary" onClick={() => setConfirmDel(false)}>Cancel</Btn>
              <Btn variant="danger" data-testid="confirm-delete-bid" onClick={doDelete}>Yes, delete</Btn>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
